import { restedBonusElementEligible, setRestedBonusElementVariant } from './rested-bonus-policy.js';
import { createNativeReader } from './loyalty-native-reader.js?v=20261005-rested-operational';

// Display evidence only. Warehouse and the signed proxy remain award authority.
export function readActiveRestedBonus(snapshot, now = Date.now()) {
  const bonus = snapshot?.restedBonus;
  if (snapshot?.schemaVersion !== 1 || snapshot.provider !== 'loyaltylion'
    || snapshot.available !== true || bonus?.status !== 'active'
    || bonus.delivery !== 'manual_adjustment' || bonus.pointsPerUnit !== 50
    || typeof bonus.observedAt !== 'string' || typeof bonus.expiresAt !== 'string') return null;
  const observed = Date.parse(bonus.observedAt);
  const expires = Date.parse(bonus.expiresAt);
  if (!Number.isFinite(observed) || !Number.isFinite(expires)
    || observed > now + 5000 || expires <= now || expires <= observed
    || expires - observed > 60_000) return null;
  return bonus;
}

// Expected account restrictions are not verification failures. Require explicit
// server proof rather than inferring inactivity from an omitted bonus field.
export function readInactiveRestedBonus(snapshot, now = Date.now()) {
  const bonus = snapshot?.restedBonus;
  if (snapshot?.schemaVersion !== 1 || snapshot.provider !== 'loyaltylion'
    || snapshot.available !== true || bonus?.status !== 'inactive'
    || !['not_launched', 'not_enrolled', 'tier_ineligible'].includes(bonus.reason)
    || bonus.delivery !== null || bonus.pointsPerUnit !== null
    || typeof bonus.observedAt !== 'string' || typeof bonus.expiresAt !== 'string') return null;
  const observed = Date.parse(bonus.observedAt);
  const expires = Date.parse(bonus.expiresAt);
  if (!Number.isFinite(observed) || !Number.isFinite(expires)
    || observed > now + 5000 || expires <= now || expires <= observed
    || expires - observed > 60_000) return null;
  return bonus;
}

const selector = '[data-loyalty-rested-bonus]';
const copySelector = '[data-loyalty-rested-copy]';
const controllerKey = Symbol.for('sigma.loyaltyRestedStatus.controller');
const updateRestedBonusVariant = (root, variant) => {
  root?.querySelectorAll?.(`${selector}:not([data-rested-variant-fixed="true"]), ${copySelector}[data-loyalty-rested-copy="variant"]`).forEach((element) => {
    setRestedBonusElementVariant(element, variant);
  });
};

const createRestedBonusController = () => {
  // The cart POST intentionally omits account-level Rested proof. Do not let
  // its pending request replace this GET, even when the bag loads at the same time.
  const readRestedStatus = createNativeReader({ delayMs: 0 });
  let snapshot = null;
  let request = null;
  let expiryTimer;
  let generation = 0;
  let lastRead = 0;
  let proofState = 'idle';

  const copyFor = (element, active) => {
    if (element.dataset.restedMember !== 'true') {
      return element.dataset.loyaltyRestedCopy === 'collection'
        ? 'Sign in to check whether the +50 bonus is available.'
        : element.dataset.loyaltyRestedCopy === 'loyalty'
          ? 'Sign in to check current bonus availability.'
          : 'Sign in to check this coffee\u2019s bonus status.';
    }

    if (proofState === 'checking' || proofState === 'idle') {
      return element.dataset.loyaltyRestedCopy === 'variant'
        ? 'Checking this coffee\u2019s bonus status\u2026'
        : 'Checking bonus availability\u2026';
    }

    if (!active) {
      const inactive = readInactiveRestedBonus(snapshot);
      if (inactive?.reason === 'not_launched') {
        return 'The Rested bonus is not available on your account yet.';
      }
      if (inactive?.reason === 'not_enrolled') {
        return 'Join Sigma Rewards to earn the Rested bonus on eligible bags.';
      }
      if (inactive?.reason === 'tier_ineligible') {
        return 'The Rested bonus does not apply to Library Subscriber accounts.';
      }
      return element.dataset.loyaltyRestedCopy === 'variant'
        ? 'Unable to check this coffee\u2019s bonus status. Try again later.'
        : 'Unable to check bonus availability. Try again later.';
    }

    if (element.dataset.loyaltyRestedCopy === 'loyalty') {
      return 'Available for eligible bags on your account.';
    }

    if (element.dataset.loyaltyRestedCopy === 'collection') {
      return 'Eligible bags earn 50 bonus points in addition to your usual purchase points.';
    }

    return 'This coffee qualifies for +50 bonus points. Changing the roast date, size or roast type may remove the bonus.';
  };

  const render = () => {
    const active = !document.hidden && readActiveRestedBonus(snapshot);
    document.querySelectorAll(selector).forEach((tag) => {
      const exactVariantReady = tag.dataset.restedVariantRequired !== 'true'
        || tag.dataset.restedVariantReady === 'true';
      // A public offer advertises the benefit. It does not prove an award.
      if (tag.dataset.restedPublicOffer === 'true') {
        tag.hidden = !restedBonusElementEligible(tag);
        return;
      }
      tag.hidden = !(active && tag.dataset.restedMember === 'true' && exactVariantReady && restedBonusElementEligible(tag));
    });
    document.querySelectorAll(copySelector).forEach((copy) => {
      copy.hidden = (copy.dataset.loyaltyRestedCopy === 'variant'
        && !restedBonusElementEligible(copy))
        || (copy.dataset.loyaltyRestedCopy === 'loyalty'
          && copy.dataset.restedMember === 'true' && Boolean(active));
      const text = copyFor(copy, active);
      if (copy.textContent.trim() !== text) copy.textContent = text;
    });
  };

  const clear = () => {
    generation++;
    request?.release();
    request = null;
    clearTimeout(expiryTimer);
    snapshot = null;
    proofState = 'idle';
    render();
  };

  const refresh = async () => {
    render();
    if (document.hidden || request || readActiveRestedBonus(snapshot) || readInactiveRestedBonus(snapshot)
      || Date.now() - lastRead < 10000
      || !document.querySelector(`${selector}[data-rested-member="true"]:not([data-rested-public-offer="true"]), ${copySelector}[data-rested-member="true"]`)) return;
    lastRead = Date.now();
    proofState = 'checking';
    render();
    const version = ++generation;
    const current = readRestedStatus({
      url: new URL('/apps/sigma-stock-alerts/loyalty/v1/native/me', window.location.origin).href,
    });
    request = current;
    try {
      const result = await current.promise;
      if (version !== generation) return;
      snapshot = result.ok ? result.snapshot : null;
      proofState = readActiveRestedBonus(snapshot) ? 'active'
        : readInactiveRestedBonus(snapshot) ? 'inactive' : 'unavailable';
      clearTimeout(expiryTimer);
      const bonus = readActiveRestedBonus(snapshot) || readInactiveRestedBonus(snapshot);
      if (bonus) expiryTimer = setTimeout(() => {
        snapshot = null;
        proofState = 'checking';
        render();
        lastRead = 0;
        refresh();
      }, Math.max(1, Date.parse(bonus.expiresAt) - Date.now()));
      render();
    } catch {
      if (version === generation) { snapshot = null; proofState = 'unavailable'; render(); }
    } finally {
      current.release();
      if (request === current) request = null;
    }
  };

  const setVariant = (root, variant) => {
    updateRestedBonusVariant(root, variant);
    render();
  };

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => { render(); refresh(); });
    observer.observe(document.querySelector('#MainContent') || document.body, { childList: true, subtree: true });
    window.addEventListener('rested-bonus-policy-change', render);
    window.addEventListener('pagehide', clear);
    window.addEventListener('pageshow', () => { lastRead = 0; refresh(); });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clear();
      else { lastRead = 0; refresh(); }
    });
    refresh();
  }

  return { render, setVariant };
};

const getRestedBonusController = () => {
  if (typeof document === 'undefined') return null;
  globalThis[controllerKey] ||= createRestedBonusController();
  return globalThis[controllerKey];
};

// Called synchronously on every selection, including unavailable combinations.
// A parent readiness animation must never leave an old variant's bonus visible.
// Pinned add-on badges retain their own exact variant's eligibility.
export function setRestedBonusVariant(root, variant) {
  const controller = getRestedBonusController();
  if (controller) controller.setVariant(root, variant);
  else updateRestedBonusVariant(root, variant);
}

getRestedBonusController();
